/**
 * 咨询会话 REST API hooks (B-1A / B-1B)
 *
 * 后端 (path 列表):
 * - GET  /app/legal/consult/sessions?side                  我的会话列表 (带 unreadCount)
 * - POST /app/legal/consult/sessions                       创建会话
 * - GET  /app/legal/consult/sessions/{sessionId}           会话详情
 * - PUT  /app/legal/consult/sessions/{sessionId}/extend    延长会话
 * - GET  /app/legal/consult/sessions/{sessionId}/messages  消息分页
 * - POST /app/legal/consult/sessions/{sessionId}/messages  发送文本消息
 * - POST .../messages/upload/image                         图片消息
 * - POST .../messages/upload/file                          文件消息
 * - GET  /client/legal/presence/users                      多用户在线状态
 * - GET  /client/legal/presence/sessions/{sessionId}       会话内成员在线状态
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { http } from '@/lib/http'
import { toNumber } from '@/lib/utils'
import { getHandoffRefetchInterval, resolveHandoffAttachmentUrl } from './utils/ai-handoff'
import type {
  AppPresenceVo,
  LegalAiConsultHandoffCreateBo,
  LegalAiConsultHandoffCreateVo,
  LegalAiConsultHandoffAttachmentAccessVo,
  LegalAiConsultHandoffVo,
  LegalChatMessageBo,
  LegalChatMessageVo,
  LegalContactExchangeAgreeBo,
  LegalContactExchangeDetailVo,
  LegalContactExchangePendingVo,
  LegalContactExchangeRequestBo,
  LegalContactExchangeResultVo,
  LegalConsultationSessionCreateBo,
  LegalConsultationSessionVo,
  LegalConsultUnreadCountVo,
  PageQuery,
  TableDataInfo,
} from '@/types/api'

/** 会话参与者过滤 (后端默认 both = 同时含我作为用户 / 律师的所有会话) */
export type ConsultSessionSide = 'user' | 'lawyer' | 'both'
export type ConsultUnreadSide = Exclude<ConsultSessionSide, 'both'>
export type ConsultSessionSource = 'ai' | 'normal'
export type ConsultSessionId = string

function normalizePathId(id: ConsultSessionId | null | undefined): string {
  return id == null ? '' : String(id).trim()
}

function readUnreadCount(value: unknown): number {
  return Math.max(0, toNumber(value))
}

function getContactExchangeSessionKey(sessionId: ConsultSessionId | null | undefined) {
  return ['consult', 'contact-exchange', 'session', normalizePathId(sessionId)] as const
}

function invalidateContactExchangeSession(
  queryClient: QueryClient,
  sessionId: ConsultSessionId | null | undefined,
) {
  const normalizedSessionId = normalizePathId(sessionId)
  if (!normalizedSessionId) return
  queryClient.invalidateQueries({ queryKey: getContactExchangeSessionKey(normalizedSessionId) })
}

/* ---------- 会话 ---------- */

/** 我的会话列表 (按 side 过滤 + 分页) */
export function useMySessions(
  side: ConsultSessionSide = 'both',
  source: ConsultSessionSource = 'normal',
  page: PageQuery = {},
  opts: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: ['consult', 'sessions', side, source, page],
    queryFn: () =>
      http.get<TableDataInfo<LegalConsultationSessionVo>>('/app/legal/consult/sessions', {
        params: { side, source, ...page },
      }),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: opts.refetchInterval,
  })
}

function getConsultUnreadEndpoint(side: ConsultUnreadSide): string {
  return side === 'user'
    ? '/client/legal/consult/sessions/unread-count'
    : '/app/legal/consult/sessions/unread-count'
}

function normalizeConsultUnreadCount(vo: LegalConsultUnreadCountVo | null | undefined): number {
  const count = readUnreadCount(vo?.totalUnreadCount)
  return count > 0 ? count : vo?.hasUnread ? 1 : 0
}

export function getConsultUnreadQueryKey(side: ConsultUnreadSide) {
  return ['consult', 'sessions', 'unread-count', side] as const
}

export function bumpConsultUnreadCache(
  queryClient: QueryClient,
  side: ConsultUnreadSide,
  sessionId: string,
  options: { amount?: number; preview?: string } = {},
): boolean {
  const amount = Math.max(0, options.amount ?? 1)
  if (amount <= 0) return false

  queryClient.setQueryData<number>(
    getConsultUnreadQueryKey(side),
    (current) => readUnreadCount(current) + amount,
  )
  let didUpdateSession = false
  queryClient.setQueriesData<TableDataInfo<LegalConsultationSessionVo>>(
    { queryKey: ['consult', 'sessions', side] },
    (data) => {
      if (!data?.rows) return data

      let changed = false
      const rows = data.rows.map((row) => {
        if (String(row.id ?? '') !== sessionId) return row
        changed = true
        didUpdateSession = true
        return {
          ...row,
          unreadCount: String(readUnreadCount(row.unreadCount) + amount),
          ...(options.preview ? { lastMessage: options.preview } : {}),
        }
      })

      return changed ? { ...data, rows } : data
    },
  )
  return didUpdateSession
}

export function clearConsultSessionUnreadCache(
  queryClient: QueryClient,
  side: ConsultUnreadSide,
  sessionId: string,
  fallbackUnreadCount = 0,
): number {
  let clearedCount = readUnreadCount(fallbackUnreadCount)

  queryClient.setQueriesData<TableDataInfo<LegalConsultationSessionVo>>(
    { queryKey: ['consult', 'sessions', side] },
    (data) => {
      if (!data?.rows) return data

      let changed = false
      const rows = data.rows.map((row) => {
        if (String(row.id ?? '') !== sessionId) return row
        const rowUnreadCount = readUnreadCount(row.unreadCount)
        clearedCount = Math.max(clearedCount, rowUnreadCount)
        if (rowUnreadCount <= 0) return row
        changed = true
        return { ...row, unreadCount: '0' }
      })

      return changed ? { ...data, rows } : data
    },
  )

  if (clearedCount > 0) {
    queryClient.setQueryData<number>(getConsultUnreadQueryKey(side), (current) =>
      Math.max(0, readUnreadCount(current) - clearedCount),
    )
  }

  return clearedCount
}

export function decrementConsultUnreadCache(
  queryClient: QueryClient,
  side: ConsultUnreadSide,
  sessionId: string,
  options: { amount?: number; preview?: string } = {},
): number {
  const amount = Math.max(0, options.amount ?? 1)
  if (amount <= 0) return 0

  let decrementedCount = 0
  queryClient.setQueriesData<TableDataInfo<LegalConsultationSessionVo>>(
    { queryKey: ['consult', 'sessions', side] },
    (data) => {
      if (!data?.rows) return data

      let changed = false
      const rows = data.rows.map((row) => {
        if (String(row.id ?? '') !== sessionId) return row
        const rowUnreadCount = readUnreadCount(row.unreadCount)
        decrementedCount = Math.min(amount, rowUnreadCount)
        const nextUnreadCount = Math.max(0, rowUnreadCount - amount)
        if (nextUnreadCount === rowUnreadCount && !options.preview) return row
        changed = true
        return {
          ...row,
          unreadCount: String(nextUnreadCount),
          ...(options.preview ? { lastMessage: options.preview } : {}),
        }
      })

      return changed ? { ...data, rows } : data
    },
  )

  const totalDecrement = decrementedCount > 0 ? decrementedCount : amount
  queryClient.setQueryData<number>(getConsultUnreadQueryKey(side), (current) =>
    Math.max(0, readUnreadCount(current) - totalDecrement),
  )

  return totalDecrement
}

/** 我的咨询会话总未读数 (菜单红点用) */
export function useConsultUnreadCount(
  side: ConsultUnreadSide,
  opts: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: getConsultUnreadQueryKey(side),
    queryFn: async () => {
      const data = await http.get<LegalConsultUnreadCountVo>(getConsultUnreadEndpoint(side), {
        params: { side },
      })
      return normalizeConsultUnreadCount(data)
    },
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    refetchInterval: opts.refetchInterval,
  })
}

/** 创建咨询会话 */
export function useCreateSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['consult', 'create-session'],
    mutationFn: async (body: LegalConsultationSessionCreateBo) =>
      http.post<LegalConsultationSessionVo>('/app/legal/consult/sessions', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consult', 'sessions'] })
      qc.invalidateQueries({ queryKey: ['consult', 'session'] })
    },
  })
}

/** AI 文书会话转人工咨询交接 */
export function useCreateAiConsultHandoff() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['consult', 'ai-handoff', 'create'],
    mutationFn: async ({
      conversationId,
      body,
    }: {
      conversationId: string
      body: LegalAiConsultHandoffCreateBo
    }) =>
      http.post<LegalAiConsultHandoffCreateVo>(
        `/app/legal/consult/ai-conversations/${encodeURIComponent(conversationId)}/handoffs`,
        body,
      ),
    onSuccess: (data) => {
      const sessionId = normalizePathId(data.consultSession?.id ?? data.handoff?.consultSessionId)
      qc.invalidateQueries({ queryKey: ['consult', 'sessions'] })
      if (sessionId) {
        qc.invalidateQueries({ queryKey: ['consult', 'session', sessionId] })
        qc.invalidateQueries({ queryKey: ['consult', 'session', sessionId, 'handoffs'] })
      }
    },
  })
}

/** 会话详情 */
export function useSessionDetail(sessionId: ConsultSessionId | undefined) {
  const normalizedSessionId = normalizePathId(sessionId)
  return useQuery({
    queryKey: ['consult', 'session', normalizedSessionId],
    queryFn: () =>
      http.get<LegalConsultationSessionVo>(
        `/app/legal/consult/sessions/${encodeURIComponent(normalizedSessionId)}`,
      ),
    enabled: normalizedSessionId !== '',
    staleTime: 30_000,
  })
}

/** 查询人工咨询会话内的 AI 交接记录 */
export function useSessionHandoffs(
  sessionId: ConsultSessionId | undefined,
  opts: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  const normalizedSessionId = normalizePathId(sessionId)
  return useQuery({
    queryKey: ['consult', 'session', normalizedSessionId, 'handoffs'],
    queryFn: () =>
      http.get<LegalAiConsultHandoffVo[]>(
        `/app/legal/consult/sessions/${encodeURIComponent(normalizedSessionId)}/handoffs`,
      ),
    enabled: (opts.enabled ?? true) && normalizedSessionId !== '',
    staleTime: 30_000,
    refetchInterval:
      opts.refetchInterval ?? ((query) => getHandoffRefetchInterval(query.state.data)),
  })
}

/** 失败后显式重试，成功后更新本会话缓存，避免影响已切换的其他会话。 */
export function useRetryAiHandoffSummary(sessionId: string | undefined) {
  const queryClient = useQueryClient()
  const normalizedSessionId = normalizePathId(sessionId)
  return useMutation({
    mutationKey: ['consult', 'ai-handoff', 'summary-retry', normalizedSessionId],
    mutationFn: (handoffId: string) =>
      http.post<LegalAiConsultHandoffVo>(
        `/app/legal/consult/handoffs/${encodeURIComponent(handoffId)}/summary/retry`,
      ),
    onSuccess: (handoff) => {
      const queryKey = ['consult', 'session', normalizedSessionId, 'handoffs']
      queryClient.setQueryData<LegalAiConsultHandoffVo[]>(queryKey, (current) =>
        current?.map((item) => (item.id === handoff.id ? handoff : item)),
      )
      void queryClient.invalidateQueries({ queryKey })
    },
  })
}

/** 临时链接始终通过交接参与者鉴权，不使用裸 OSS ID 查询文件。 */
export function fetchHandoffAttachmentAccess(
  handoffId: string,
  attachmentId: string,
  signal?: AbortSignal,
) {
  return http.get<LegalAiConsultHandoffAttachmentAccessVo>(
    `/app/legal/consult/handoffs/${encodeURIComponent(handoffId)}/attachments/${encodeURIComponent(attachmentId)}/access`,
    { signal, silentError: true },
  )
}

/** 预览和下载前重新获取访问权，避免复用已经过期的签名地址。 */
export async function fetchHandoffAttachmentAsset(
  handoffId: string,
  attachmentId: string,
  signal?: AbortSignal,
) {
  const access = await fetchHandoffAttachmentAccess(handoffId, attachmentId, signal)
  const url = resolveHandoffAttachmentUrl(access.url ?? '', window.location.origin)
  // access 已校验交接参与关系，OSS 返回临时签名地址；同源代理也不重复拼接 API 前缀。
  const response = await fetch(url.href, {
    signal,
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  })
  if (!response.ok) throw new Error('材料加载失败，请重新加载')
  const blob = await response.blob()

  if (blob.type.includes('json')) {
    const payload = await blob.text()
    try {
      const result = JSON.parse(payload) as { code?: number; msg?: string }
      if (typeof result.code === 'number' && result.code !== 200) {
        throw new Error(result.msg || '材料加载失败，请重新加载')
      }
    } catch (error) {
      if (error instanceof SyntaxError) return { blob, access }
      throw error
    }
  }
  return { blob, access }
}

export async function downloadHandoffAttachment(
  handoffId: string,
  attachmentId: string,
  fileName: string | null | undefined,
) {
  const { blob, access } = await fetchHandoffAttachmentAsset(handoffId, attachmentId)
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = (access.fileName || fileName || '咨询材料').replace(/[\\/:*?"<>|\r\n\t]/g, '_')
  anchor.rel = 'noopener'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

/** 延长会话 */
export function useExtendSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['consult', 'extend'],
    mutationFn: async (sessionId: ConsultSessionId) =>
      http.put<void>(
        `/app/legal/consult/sessions/${encodeURIComponent(normalizePathId(sessionId))}/extend`,
      ),
    onSuccess: (_, sessionId) =>
      qc.invalidateQueries({ queryKey: ['consult', 'session', normalizePathId(sessionId)] }),
  })
}

/* ---------- 消息 ---------- */

/** 拉历史消息 (分页) */
export function useSessionMessages(sessionId: ConsultSessionId | undefined, page: PageQuery = {}) {
  const normalizedSessionId = normalizePathId(sessionId)
  return useQuery({
    queryKey: ['consult', 'messages', normalizedSessionId, page],
    queryFn: () =>
      http.get<TableDataInfo<LegalChatMessageVo>>(
        `/app/legal/consult/sessions/${encodeURIComponent(normalizedSessionId)}/messages`,
        { params: page },
      ),
    enabled: normalizedSessionId !== '',
    staleTime: 0,
  })
}

/** 发送文本消息 */
export function useSendMessage(sessionId: ConsultSessionId | undefined) {
  const qc = useQueryClient()
  const normalizedSessionId = normalizePathId(sessionId)
  return useMutation({
    mutationKey: ['consult', 'send', normalizedSessionId],
    mutationFn: async (body: LegalChatMessageBo) => {
      if (!normalizedSessionId) throw new Error('未指定会话')
      return http.post<LegalChatMessageVo>(
        `/app/legal/consult/sessions/${encodeURIComponent(normalizedSessionId)}/messages`,
        body,
      )
    },
    onSuccess: () => {
      if (normalizedSessionId) {
        qc.invalidateQueries({ queryKey: ['consult', 'messages', normalizedSessionId] })
      }
    },
  })
}

/* ---------- 联系方式交换 ---------- */

export function usePendingContactExchange(
  sessionId: ConsultSessionId | undefined,
  opts: { enabled?: boolean; refetchInterval?: number } = {},
) {
  const normalizedSessionId = normalizePathId(sessionId)
  return useQuery({
    queryKey: [...getContactExchangeSessionKey(normalizedSessionId), 'pending'],
    queryFn: () =>
      http.get<LegalContactExchangePendingVo | null>(
        `/app/legal/contact-exchange/sessions/${encodeURIComponent(normalizedSessionId)}/pending`,
      ),
    enabled: (opts.enabled ?? true) && normalizedSessionId !== '',
    staleTime: 0,
    refetchInterval: opts.refetchInterval,
  })
}

export function useRequestContactExchange(sessionId: ConsultSessionId | undefined) {
  const qc = useQueryClient()
  const normalizedSessionId = normalizePathId(sessionId)
  return useMutation({
    mutationKey: ['consult', 'contact-exchange', 'request', normalizedSessionId],
    mutationFn: async (body: LegalContactExchangeRequestBo) => {
      if (!normalizedSessionId) throw new Error('未指定会话')
      return http.post<LegalContactExchangeResultVo>(
        `/app/legal/contact-exchange/sessions/${encodeURIComponent(normalizedSessionId)}/request`,
        body,
      )
    },
    onSuccess: () => {
      invalidateContactExchangeSession(qc, normalizedSessionId)
    },
  })
}

export function useAgreeContactExchange() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['consult', 'contact-exchange', 'agree'],
    mutationFn: async ({
      exchangeId,
      body,
    }: {
      exchangeId: ConsultSessionId
      body: LegalContactExchangeAgreeBo
    }) =>
      http.post<LegalContactExchangeDetailVo>(
        `/app/legal/contact-exchange/${encodeURIComponent(normalizePathId(exchangeId))}/agree`,
        body,
      ),
    onSuccess: (detail) => {
      invalidateContactExchangeSession(qc, detail.sessionId)
      qc.invalidateQueries({ queryKey: ['consult', 'messages', normalizePathId(detail.sessionId)] })
    },
  })
}

export function useRejectContactExchange() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['consult', 'contact-exchange', 'reject'],
    mutationFn: async (exchangeId: ConsultSessionId) =>
      http.post<LegalContactExchangeResultVo>(
        `/app/legal/contact-exchange/${encodeURIComponent(normalizePathId(exchangeId))}/reject`,
      ),
    onSuccess: (result) => invalidateContactExchangeSession(qc, result.sessionId),
  })
}

export function useCancelContactExchange() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['consult', 'contact-exchange', 'cancel'],
    mutationFn: async (exchangeId: ConsultSessionId) =>
      http.post<LegalContactExchangeResultVo>(
        `/app/legal/contact-exchange/${encodeURIComponent(normalizePathId(exchangeId))}/cancel`,
      ),
    onSuccess: (result) => invalidateContactExchangeSession(qc, result.sessionId),
  })
}

export function useContactExchangeDetail() {
  return useMutation({
    mutationKey: ['consult', 'contact-exchange', 'detail'],
    mutationFn: async (exchangeId: ConsultSessionId) =>
      http.get<LegalContactExchangeDetailVo>(
        `/app/legal/contact-exchange/${encodeURIComponent(normalizePathId(exchangeId))}/detail`,
      ),
  })
}

/* ---------- 在线状态 (presence) ---------- */

/** 多用户在线状态 */
export function useUsersPresence(
  userIds: string[],
  opts: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: ['consult', 'presence-users', userIds],
    queryFn: () =>
      http.get<TableDataInfo<AppPresenceVo>>('/client/legal/presence/users', {
        params: { ids: userIds.join(',') },
      }),
    enabled: (opts.enabled ?? true) && userIds.length > 0,
    staleTime: 30_000,
    refetchInterval: opts.refetchInterval,
  })
}

/** 单会话内成员在线状态 */
export function useSessionPresence(sessionId: ConsultSessionId | undefined) {
  const normalizedSessionId = normalizePathId(sessionId)
  return useQuery({
    queryKey: ['consult', 'presence-session', normalizedSessionId],
    queryFn: () =>
      http.get<AppPresenceVo[]>(
        `/client/legal/presence/sessions/${encodeURIComponent(normalizedSessionId)}`,
      ),
    enabled: normalizedSessionId !== '',
    staleTime: 30_000,
  })
}
